import { Image } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AnswerQuestionsButtonContainer } from './AnswerQuestionsButtonContainer';
import { NewSearchButtonContainer } from './NewSearchButtonContainer';
import { SendMoreButtonContainer } from './SendMoreButtonContainer';
import { ShowAllButtonContainer } from './ShowAllButtonContainer';
import { StopSearchButtonContainer } from './StopSearchButtonContainer';
import { TunePeriodicityButtonContainer } from './TunePeriodicityButtonContainer';
import { trackActionButtonClick } from './tracking';
import { openChatbotFiltersModal } from '../../actions/chatbotFiltersModal';
import { openFeedbackChatbotModal } from '../../actions/feedbackChatbotModal';
import { ChatbotButton } from '../../components/ChatbotButton';
import { ChatbotButtonsLayout } from '../../components/ChatbotButtonsLayout';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { EChatbotButtons, useChatbotButtons } from '../../hooks/useChatbotButtons';
import { chatbotSelector, currentOpenChatIDSelector } from '../../selectors';
import { getChatbotActionButtons } from '../../thunks/chatbot/getChatbotActionButtons';
import { sendChatbotMessage } from '../../thunks/chatbot/sendChatbotMessage';
import { IChatbotActionButtonSchema } from '../../types/chatbot';
import { ETelemetryEvents } from '../../types/telemetry';
import { useApplicationContext } from '../../utils/applicationContext';
import { useRuntimeSetting } from '../../hooks/useRuntimeSetting';

const CHATBOT_BUTTONS: { [key in EChatbotButtons]: React.ReactNode } = {
  [EChatbotButtons.AnswerQuestions]: <AnswerQuestionsButtonContainer key={EChatbotButtons.AnswerQuestions} />,
  [EChatbotButtons.ShowAll]: <ShowAllButtonContainer key={EChatbotButtons.ShowAll} />,
  [EChatbotButtons.NewSearch]: <NewSearchButtonContainer key={EChatbotButtons.NewSearch} />,
  [EChatbotButtons.StopSearch]: <StopSearchButtonContainer key={EChatbotButtons.StopSearch} />,
  [EChatbotButtons.TunePeriodicity]: <TunePeriodicityButtonContainer key={EChatbotButtons.TunePeriodicity} />,
  [EChatbotButtons.SendMore]: <SendMoreButtonContainer key={EChatbotButtons.SendMore} />,
};

export const ChatbotButtonsContainer: React.FC = () => {
  const { telemetry } = useApplicationContext();
  const dispatch = useDispatch();

  const isChatbotGPTEnabled = useRuntimeSetting<boolean>('frontend-chats.chatbotGPT.Enabled', false);
  const fixedButtons = useChatbotButtons(isChatbotGPTEnabled);
  const chatId = useSelector(currentOpenChatIDSelector);
  const { actionButtons, messageSending: isMessageSending } = useSelector(chatbotSelector);

  const onClick = React.useCallback(
    (actionButtonChatbot: IChatbotActionButtonSchema) => {
      if (isMessageSending) {
        return;
      }

      const { analyticsType, payload, actionType, link, inputText, text } = actionButtonChatbot;
      trackActionButtonClick(analyticsType);

      if (actionType === 'openSurvey') {
        telemetry.increment(ETelemetryEvents.FeedbackChatbotModalShowButton);
        dispatch(openFeedbackChatbotModal());

        return;
      }

      if (actionType === 'payload') {
        dispatch(getChatbotActionButtons(payload, text));

        return;
      }

      if (actionType === 'sendMessage') {
        dispatch(sendChatbotMessage(inputText || ''));

        return;
      }

      if (actionType === 'deeplink' && link?.includes('chat-bot-profiling')) {
        dispatch(openChatbotFiltersModal());

        return;
      }

      if (link) {
        window.open(link, '_blank');
      }
    },
    [dispatch, isMessageSending, telemetry],
  );

  const buttons =
    chatId === CHATBOT_ID && isChatbotGPTEnabled
      ? actionButtons.map((actionButtonChatbot, index) => {
          const { text, leftIcon, rightIcon, isSelected } = actionButtonChatbot;

          return (
            <ChatbotButton
              data-name="ChatbotButtonsContainer"
              key={`chatbot-action-button-${index}`}
              theme={isSelected ? 'fill_primary' : 'fill_white_primary'}
              beforeIcon={
                leftIcon ? (
                  <Image src={leftIcon} width={16} height={16} preloader={false} borderRadius="0" />
                ) : undefined
              }
              afterIcon={
                rightIcon ? (
                  <Image src={rightIcon} width={16} height={16} preloader={false} borderRadius="0" />
                ) : undefined
              }
              onClick={onClick}
              actionButton={actionButtonChatbot}
              disabled={isMessageSending}
            >
              {text}
            </ChatbotButton>
          );
        })
      : fixedButtons.map(button => CHATBOT_BUTTONS[button]);

  if (!buttons.length) {
    return null;
  }

  return <ChatbotButtonsLayout>{buttons}</ChatbotButtonsLayout>;
};
